import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Trash2, CalendarDays, Clock, Users, Mail, Phone, XCircle, ChevronDown, ChevronUp, Pencil, AlertTriangle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

type EntityType = "seu_ze" | "seu_lucifer";

interface AppointmentSlot {
  id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  is_active: boolean;
  entity_type: EntityType;
}

interface Appointment {
  id: string;
  slot_id: string;
  appointment_date: string;
  appointment_time: string;
  status: string;
  user_id: string;
  entity_type: EntityType;
  profile?: {
    full_name: string;
    email: string;
    phone: string | null;
  };
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
}

const ENTITY_NAMES: Record<EntityType, string> = {
  seu_ze: "Seu Zé Pelintra",
  seu_lucifer: "Seu Lúcifer",
};

export const AppointmentSlotsTab = () => {
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState("18:00");
  const [endTime, setEndTime] = useState("21:00");
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());
  const [selectedEntity, setSelectedEntity] = useState<EntityType>("seu_ze");
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [slotToEdit, setSlotToEdit] = useState<AppointmentSlot | null>(null);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data: slotsData } = await supabase
        .from("appointment_slots")
        .select("*")
        .order("slot_date", { ascending: true });

      const { data: appointmentsData } = await supabase
        .from("appointments")
        .select("*")
        .order("appointment_date", { ascending: true });

      // Fetch profiles for appointments
      if (appointmentsData && appointmentsData.length > 0) {
        const userIds = [...new Set(appointmentsData.map((a) => a.user_id))];
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("*")
          .in("id", userIds);

        const profilesMap = new Map<string, Profile>();
        profilesData?.forEach((p) => profilesMap.set(p.id, p));

        const appointmentsWithProfiles = appointmentsData.map((appt) => ({
          ...appt,
          entity_type: (appt.entity_type as EntityType) || "seu_ze",
          profile: profilesMap.get(appt.user_id),
        }));

        setAppointments(appointmentsWithProfiles);
      } else {
        setAppointments([]);
      }

      // Cast entity_type for slots
      const slotsWithEntity = (slotsData || []).map((slot) => ({
        ...slot,
        entity_type: (slot.entity_type as EntityType) || "seu_ze",
      }));

      setSlots(slotsWithEntity);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSlots = (start: string, end: string) => {
    const startParts = start.split(":");
    const endParts = end.split(":");
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    return Math.floor((endMinutes - startMinutes) / 60);
  };

  // Filter slots by selected entity
  const filteredSlots = slots.filter((slot) => slot.entity_type === selectedEntity);

  const handleCreateSlot = async () => {
    if (!selectedDate) {
      toast({
        title: "Selecione uma data",
        description: "Por favor, selecione uma data no calendário",
        variant: "destructive",
      });
      return;
    }

    const slotsCount = calculateSlots(startTime, endTime);
    if (slotsCount <= 0) {
      toast({
        title: "Horário inválido",
        description: "O horário de término deve ser posterior ao de início",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const dateStr = format(selectedDate, "yyyy-MM-dd");

      // Check if slot already exists for this date AND entity
      const existingSlot = slots.find((s) => s.slot_date === dateStr && s.entity_type === selectedEntity);
      if (existingSlot) {
        toast({
          title: "Data já cadastrada",
          description: `Já existe uma disponibilidade para ${ENTITY_NAMES[selectedEntity]} nesta data. Delete-a primeiro para criar uma nova.`,
          variant: "destructive",
        });
        setSaving(false);
        return;
      }

      const { error } = await supabase.from("appointment_slots").insert({
        slot_date: dateStr,
        start_time: startTime + ":00",
        end_time: endTime + ":00",
        slot_duration_minutes: 60,
        is_active: true,
        created_by: user?.id,
        entity_type: selectedEntity,
      });

      if (error) throw error;

      toast({
        title: "Disponibilidade criada!",
        description: `${slotsCount} vagas criadas para ${ENTITY_NAMES[selectedEntity]} em ${format(selectedDate, "dd 'de' MMMM", { locale: ptBR })}`,
      });

      await fetchData();
      setSelectedDate(undefined);
    } catch (error: any) {
      toast({
        title: "Erro ao criar disponibilidade",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlot = async (slotId: string) => {
    if (!confirm("Tem certeza que deseja excluir esta disponibilidade? Agendamentos existentes serão cancelados.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("appointment_slots")
        .delete()
        .eq("id", slotId);

      if (error) throw error;

      toast({
        title: "Disponibilidade excluída",
        description: "A disponibilidade foi removida com sucesso",
      });

      await fetchData();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const openCancelDialog = (appointment: Appointment) => {
    setAppointmentToCancel(appointment);
    setCancelReason("");
    setCancelDialogOpen(true);
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;

    setCancelling(true);

    try {
      const { data, error } = await supabase.functions.invoke("admin-cancel-appointment", {
        body: {
          appointment_id: appointmentToCancel.id,
          reason: cancelReason || undefined,
        },
      });

      if (error) throw error;

      toast({
        title: "Agendamento cancelado",
        description: data.emailSent 
          ? "O consulente foi notificado por e-mail." 
          : "Agendamento cancelado (e-mail não enviado).",
      });

      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setCancelReason("");
      await fetchData();
    } catch (error: any) {
      console.error("Error cancelling appointment:", error);
      toast({
        title: "Erro ao cancelar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const toggleSlotExpanded = (slotId: string) => {
    const newExpanded = new Set(expandedSlots);
    if (newExpanded.has(slotId)) {
      newExpanded.delete(slotId);
    } else {
      newExpanded.add(slotId);
    }
    setExpandedSlots(newExpanded);
  };

  const formatTime12h = (time: string) => {
    const [hours, mins] = time.split(":");
    const h = parseInt(hours);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${mins} ${ampm}`;
  };

  const getBookedCount = (slotId: string, date: string) => {
    return appointments.filter(
      (a) => a.slot_id === slotId && a.appointment_date === date && a.status !== "cancelled"
    ).length;
  };

  const getAppointmentsForSlot = (slotId: string, date: string) => {
    return appointments.filter(
      (a) => a.slot_id === slotId && a.appointment_date === date && a.status !== "cancelled"
    );
  };

  const openEditDialog = (slot: AppointmentSlot) => {
    setSlotToEdit(slot);
    setEditStartTime(slot.start_time.slice(0, 5));
    setEditEndTime(slot.end_time.slice(0, 5));
    setEditDialogOpen(true);
  };

  const getBookedTimesForSlot = (slotId: string, date: string) => {
    return appointments
      .filter((a) => a.slot_id === slotId && a.appointment_date === date && a.status !== "cancelled")
      .map((a) => a.appointment_time.slice(0, 5));
  };

  const timeToMinutes = (time: string) => {
    const [hours, mins] = time.split(":").map(Number);
    return hours * 60 + mins;
  };

  const validateEditTimes = () => {
    if (!slotToEdit) return { valid: false, message: "Slot não encontrado" };

    const newStartMinutes = timeToMinutes(editStartTime);
    const newEndMinutes = timeToMinutes(editEndTime);

    if (newEndMinutes <= newStartMinutes) {
      return { valid: false, message: "O horário de término deve ser posterior ao de início" };
    }

    const bookedTimes = getBookedTimesForSlot(slotToEdit.id, slotToEdit.slot_date);
    
    if (bookedTimes.length === 0) {
      return { valid: true, message: "" };
    }

    // Check if any booked time falls outside the new range
    for (const bookedTime of bookedTimes) {
      const bookedMinutes = timeToMinutes(bookedTime);
      if (bookedMinutes < newStartMinutes || bookedMinutes >= newEndMinutes) {
        return { 
          valid: false, 
          message: `Não é possível alterar os horários. Existe um agendamento às ${formatTime12h(bookedTime)} que ficaria fora do novo intervalo.` 
        };
      }
    }

    return { valid: true, message: "" };
  };

  const handleEditSlot = async () => {
    if (!slotToEdit) return;

    const validation = validateEditTimes();
    if (!validation.valid) {
      toast({
        title: "Horários inválidos",
        description: validation.message,
        variant: "destructive",
      });
      return;
    }

    setEditSaving(true);

    try {
      const { error } = await supabase
        .from("appointment_slots")
        .update({
          start_time: editStartTime + ":00",
          end_time: editEndTime + ":00",
        })
        .eq("id", slotToEdit.id);

      if (error) throw error;

      const newSlotsCount = calculateSlots(editStartTime, editEndTime);

      toast({
        title: "Disponibilidade atualizada!",
        description: `Horários alterados. Agora há ${newSlotsCount} vagas disponíveis.`,
      });

      setEditDialogOpen(false);
      setSlotToEdit(null);
      await fetchData();
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEditSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Entity Tabs */}
      <Tabs value={selectedEntity} onValueChange={(v) => setSelectedEntity(v as EntityType)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="seu_ze" className="flex items-center gap-2">
            <span>Seu Zé Pelintra</span>
          </TabsTrigger>
          <TabsTrigger value="seu_lucifer" className="flex items-center gap-2">
            <span>Seu Lúcifer</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={selectedEntity} className="mt-6 space-y-6">
          {/* Create New Slot */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-display text-primary flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Adicionar Disponibilidade - {ENTITY_NAMES[selectedEntity]}
              </CardTitle>
              <CardDescription>
                Selecione uma data e defina os horários de atendimento para {ENTITY_NAMES[selectedEntity]}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">Data</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={ptBR}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    className="rounded-md border"
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="start-time">Horário de Início</Label>
                    <Input
                      id="start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-time">Horário de Término</Label>
                    <Input
                      id="end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  {selectedDate && (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Resumo:</p>
                      <p className="font-medium">
                        {format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </p>
                      <p className="text-sm">
                        {formatTime12h(startTime)} até {formatTime12h(endTime)}
                      </p>
                      <p className="text-sm text-accent font-semibold mt-2">
                        {calculateSlots(startTime, endTime)} vagas disponíveis
                      </p>
                      <Badge variant="outline" className="mt-2">
                        {ENTITY_NAMES[selectedEntity]}
                      </Badge>
                    </div>
                  )}
                  <Button
                    onClick={handleCreateSlot}
                    disabled={!selectedDate || saving}
                    className="w-full"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Criar Disponibilidade
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Existing Slots */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-display text-primary flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Disponibilidades - {ENTITY_NAMES[selectedEntity]}
              </CardTitle>
              <CardDescription>
                Gerencie as datas e horários disponíveis para {ENTITY_NAMES[selectedEntity]}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredSlots.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma disponibilidade cadastrada para {ENTITY_NAMES[selectedEntity]}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredSlots.map((slot) => {
                    const totalSlots = calculateSlots(
                      slot.start_time.slice(0, 5),
                      slot.end_time.slice(0, 5)
                    );
                    const bookedCount = getBookedCount(slot.id, slot.slot_date);
                    const slotAppointments = getAppointmentsForSlot(slot.id, slot.slot_date);
                    const isExpanded = expandedSlots.has(slot.id);

                    return (
                      <div
                        key={slot.id}
                        className="border rounded-lg bg-card overflow-hidden"
                      >
                        {/* Slot Header */}
                        <div className="p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => bookedCount > 0 && toggleSlotExpanded(slot.id)}
                          >
                            <div className="flex items-center gap-2">
                              <CalendarDays className="w-5 h-5 text-primary" />
                              <span className="font-display text-lg font-semibold">
                                {format(parseISO(slot.slot_date), "dd 'de' MMMM 'de' yyyy", {
                                  locale: ptBR,
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatTime12h(slot.start_time.slice(0, 5))} -{" "}
                                {formatTime12h(slot.end_time.slice(0, 5))}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {bookedCount}/{totalSlots} agendados
                              </span>
                              {bookedCount > 0 && (
                                <span className="flex items-center gap-1 text-primary">
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                  Ver detalhes
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-primary hover:text-primary hover:bg-primary/10"
                              onClick={() => openEditDialog(slot)}
                              title="Editar horários"
                            >
                              <Pencil className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteSlot(slot.id)}
                              title="Excluir disponibilidade"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>

                        {/* Appointments Table */}
                        {isExpanded && slotAppointments.length > 0 && (
                          <div className="border-t bg-muted/20 p-4">
                            <h4 className="font-semibold mb-3 flex items-center gap-2">
                              <Users className="w-4 h-4 text-primary" />
                              Consulentes Agendados
                            </h4>
                            <div className="rounded-md border bg-background overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Horário</TableHead>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>E-mail</TableHead>
                                    <TableHead>Telefone</TableHead>
                                    <TableHead className="text-right">Ações</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {slotAppointments.map((appt) => (
                                    <TableRow key={appt.id}>
                                      <TableCell className="font-medium">
                                        {formatTime12h(appt.appointment_time.slice(0, 5))}
                                      </TableCell>
                                      <TableCell>
                                        {appt.profile?.full_name || "N/A"}
                                      </TableCell>
                                      <TableCell>
                                        <span className="flex items-center gap-1">
                                          <Mail className="w-3 h-3 text-muted-foreground" />
                                          {appt.profile?.email || "N/A"}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <span className="flex items-center gap-1">
                                          <Phone className="w-3 h-3 text-muted-foreground" />
                                          {appt.profile?.phone || "N/A"}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                          onClick={() => openCancelDialog(appt)}
                                        >
                                          <XCircle className="w-4 h-4 mr-1" />
                                          Cancelar
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Agendamento</DialogTitle>
            <DialogDescription>
              {appointmentToCancel?.profile?.full_name && (
                <span className="block mt-2">
                  Consulente: <strong>{appointmentToCancel.profile.full_name}</strong>
                </span>
              )}
              {appointmentToCancel && (
                <>
                  <span className="block">
                    Horário: <strong>{formatTime12h(appointmentToCancel.appointment_time.slice(0, 5))}</strong>
                    {" - "}
                    {format(parseISO(appointmentToCancel.appointment_date), "dd/MM/yyyy")}
                  </span>
                  <span className="block">
                    Entidade: <strong>{ENTITY_NAMES[appointmentToCancel.entity_type]}</strong>
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="cancel-reason">Motivo do cancelamento (opcional)</Label>
            <Textarea
              id="cancel-reason"
              placeholder="Informe o motivo do cancelamento para o consulente..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="mt-2"
              rows={3}
            />
            <p className="text-sm text-muted-foreground mt-2">
              O consulente receberá um e-mail com o aviso de cancelamento e solicitação de reagendamento.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelling}
            >
              Voltar
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelAppointment}
              disabled={cancelling}
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Cancelando...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Confirmar Cancelamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Slot Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Disponibilidade</DialogTitle>
            <DialogDescription>
              {slotToEdit && (
                <>
                  <span className="block mt-2">
                    Data: <strong>{format(parseISO(slotToEdit.slot_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</strong>
                  </span>
                  <span className="block">
                    Entidade: <strong>{ENTITY_NAMES[slotToEdit.entity_type]}</strong>
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {slotToEdit && getBookedTimesForSlot(slotToEdit.id, slotToEdit.slot_date).length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800 dark:text-amber-200">Atenção: Existem agendamentos</p>
                    <p className="text-amber-700 dark:text-amber-300 mt-1">
                      Horários com consultas agendadas: {getBookedTimesForSlot(slotToEdit.id, slotToEdit.slot_date).map(t => formatTime12h(t)).join(", ")}
                    </p>
                    <p className="text-amber-600 dark:text-amber-400 mt-1">
                      Os novos horários devem incluir todos os agendamentos existentes.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="edit-start-time">Horário de Início</Label>
              <Input
                id="edit-start-time"
                type="time"
                value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-end-time">Horário de Término</Label>
              <Input
                id="edit-end-time"
                type="time"
                value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)}
                className="mt-1"
              />
            </div>

            {editStartTime && editEndTime && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Novo resumo:</p>
                <p className="text-sm">
                  {formatTime12h(editStartTime)} até {formatTime12h(editEndTime)}
                </p>
                <p className="text-sm text-accent font-semibold mt-2">
                  {calculateSlots(editStartTime, editEndTime)} vagas disponíveis
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              disabled={editSaving}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditSlot}
              disabled={editSaving}
            >
              {editSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Salvando...
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
